import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, CheckCircle2 } from 'lucide-react';
import toast from '../utils/toast';
import DashboardLayout from '../components/Layout/DashboardLayout';
import ConsentMarkdown from '../components/Assessments/ConsentMarkdown';
import { talentAssessmentsAPI } from '../services/api';

/**
 * Big Five Profile — consent screen.
 *
 * Renders the active consent template's Markdown body. The user must
 * explicitly check "I agree" before they can proceed. On agreement we
 * record consent server-side, then start (or resume) a session and
 * navigate the user into the take page.
 */
const SelfServiceBigFiveConsent = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [template, setTemplate] = useState(null);

  const loadTemplate = useCallback(async () => {
    try {
      const { data } = await talentAssessmentsAPI.getBigFiveConsentTemplate('en');
      setTemplate(data);
    } catch (err) {
      console.error('Failed to load consent template', err);
      toast.error('Could not load the consent text. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTemplate(); }, [loadTemplate]);

  const handleAgree = async () => {
    if (!agreed || submitting) return;
    setSubmitting(true);
    try {
      await talentAssessmentsAPI.recordBigFiveConsent('en');
      const { data: session } = await talentAssessmentsAPI.startBigFiveSession({});
      toast.success('Consent recorded. Let\'s begin.');
      navigate(`/self-service/big-five/take/${session.id}`);
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'RETAKE_NOT_YET_AVAILABLE') {
        toast.error(err.response?.data?.detail || 'Re-take not yet available.');
        navigate('/self-service/big-five');
      } else {
        toast.error('Could not record consent. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-blue-800 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6 bg-gray-50 min-h-screen pb-32">
        {/* Sticky header */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
          <div className="flex items-center gap-4">
            <Link
              to="/self-service/big-five"
              aria-label="Back to Big Five Profile"
              title="Back to Big Five Profile"
              className="icon-button flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="min-w-0">
              <h1 className="text-subsection-header truncate">
                {template?.title || 'Consent'}
              </h1>
              <p className="text-body mt-1">
                Please read carefully before agreeing.
              </p>
            </div>
          </div>
        </div>

        {/* Consent body */}
        <div className="bg-white rounded-2xl shadow-lg p-5 sm:p-7 lg:p-8">
          <ConsentMarkdown markdown={template?.body_markdown || ''} />
          {template?.version && (
            <div className="mt-6 pt-4 border-t border-gray-200 text-xs text-gray-500">
              Consent version {template.version}
            </div>
          )}
        </div>

        {/* Sticky agree footer */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-2xl">
          <div className="max-w-5xl mx-auto p-4 sm:p-5 space-y-3">
            <label className="flex items-start gap-3 cursor-pointer select-none group">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1 w-5 h-5 rounded border-gray-300 text-blue-800 focus:ring-blue-500 cursor-pointer flex-shrink-0"
              />
              <span className="text-sm text-left text-gray-800 group-hover:text-gray-900">
                I have read and understood the above. I voluntarily agree to participate in the Big Five Profile and to the processing of my responses as described.
              </span>
            </label>
            <div className="flex justify-end gap-2">
              <Link
                to="/self-service/big-five"
                className="btn btn-outline"
              >
                Not now
              </Link>
              <button
                type="button"
                onClick={handleAgree}
                disabled={!agreed || submitting}
                className="btn btn-primary"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    Agree &amp; begin
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SelfServiceBigFiveConsent;
